/**
 * The purpose of the notifications events are to notify our users, and
 * to indicate state changes around our platform. However the notifications
 * event type has been around for some time and not all events defined here
 * service the purpose of "notifying" users.
 *
 * Websocket notifications events are websocket events whose `parent_type`
 * is `notifications`.
 *
 * Event models that live here: `{ parent_type: 'notifications' }`
 */
import { UndefinedFieldsToNull } from '@freelancer/types';
import { UpgradeType } from '@freelancer/ui/upgrade-tag';
import {
  CurrencyApi,
  JobApi,
  ReactionApi,
  TimezoneApi,
} from 'api-typings/common/common';
import {
  ContestStatusApi,
  ContestTypeApi,
  ContestUpgradeApi,
  EntryUpgradesApi,
} from 'api-typings/contests/contests';
import { DriveFileApi } from 'api-typings/drive/drive';
import { FeedItemApi } from 'api-typings/feed/feed';
import { GroupApi, GroupMemberApi } from 'api-typings/groups/groups';
import { ThreadApi } from 'api-typings/messages/messages_types';
import {
  CartItemApi,
  HourlyContractApi,
  InvoiceStatusApi,
} from 'api-typings/payments/payments';
import { PostApi } from 'api-typings/posts/posts';
import {
  BidAwardStatusApi,
  BidCompleteStatusApi,
  BidEditRequestStatusApi,
  IpContractApi,
  MilestoneCancelRequestReasonApi,
  MilestoneReasonApi,
  MilestoneStatusApi,
  ProjectCollaborationApi,
  ProjectCollaborationPermissionApi,
  ProjectStatusApi,
  ProjectSubStatusApi,
  ProjectTitleEditRequestStatusApi,
  ProjectTypeApi,
} from 'api-typings/projects/projects';
import {
  AgentApi,
  AgentSessionApi,
  AgentSessionsGetResultApi,
  SessionApi,
} from 'api-typings/support/support';
import { TaskApi } from 'api-typings/tasklist/tasklist';
import {
  BalanceApi,
  NetPromoterScoreSourceApi,
  NetPromoterScoreTypeApi,
  SecurePhoneGetResultApi,
  ShowcaseSourceTypeApi,
  UserStatusApi,
} from 'api-typings/users/users';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { QueueEventType } from '../../collections/agent-sessions/agent-sessions.model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { BudgetMinMaxBackend } from '../../collections/budget-min-max/budget-min-max.backend-model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import {
  ContestEntryFile,
  ContestEntryStatusWebsocketEvent,
} from '../../collections/contest-view-entries/contest-view-entries.model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { ContestUpgrade } from '../../collections/contests/contests.model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import {
  Currency,
  CurrencyAjax,
} from '../../collections/currencies/currencies.model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { DriveFileContext } from '../../collections/drive-files/drive-files.model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { ClientViewedBidNotificationType } from '../../collections/insights-bids/insights-bids.model';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { VerificationRequestAjax } from '../../collections/verification-request/verification-request.backend-model';
import { TimeSeconds, TimestampSeconds } from '../time/time.model';
import { BaseServerData } from './server.types';

export enum KYCStatus {
  APPROVED = 'approved',
  DECLINED = 'declined',
  PENDING = 'pending',
  RECOMMENDED = 'recommended',
  REQUIRED = 'required',
}

export enum NotificationProjectType {
  HIRE_ME = 'hireMe',
  NORMAL = 'normal',
}

export enum QHPCreateType {
  INLINE_REHIRE = 'inline-rehire',
  NORMAL = 'normal',
}

/**
 * Base Notifications event definition which all notifications events should
 * extend from.
 */
export interface BaseServerNotificationsData extends TimestampSeconds {
  readonly id: string;
  readonly parent_type: 'notifications';
  readonly type: string;
}

/**
 * Unsupported notifications events to be ignored.
 */
export interface WebsocketDeprecatedNotificationsEvent
  extends BaseServerNotificationsData {
  readonly type:
    | 'bidHidden' // don't remove this unless elastic search is cleaned
    | 'contactRequestReceived'
    | 'contactRequestSent'
    | 'sitesBinCreated'
    | 'sitesHandOverMessageSent'
    | 'sitesListingStatusChanged'
    | 'sitesMessageSent'
    | 'sitesOfferCreated'
    | 'tasklist_create'; // Renamed to tasklistCreateV1
  readonly data: any;
}

/**
 * Events that are unknown, or have yet to be defined.
 *
 * Basically everything in `event_notify.php` that doesn't have a
 * corresponding type in this file ends up here.
 */
export interface WebsocketUnknownNotificationsEvent
  extends BaseServerNotificationsData {
  readonly type:
    | 'aborted'
    | 'adminWarningUserBidRestriction'
    | 'banUserFromBidding'
    | 'bidsRunningOut'
    | 'buyFreeMarketService'
    | 'collaboratorAddedViaChat'
    | 'collaboratorsUpdatedRefreshChat'
    | 'communityPromoNoti'
    | 'contactsAutoAddedReceiver'
    | 'contactsAutoAddedSender'
    | 'contestCompleteSellEntryUpsell'
    | 'contestLockedEmployer'
    | 'contestLockedFreelancer'
    | 'contestUpsellFeaturedUpgrade'
    | 'counterOffer'
    | 'deleteFile'
    | 'downgradeToFreeMembershipNoti'
    | 'draftContestRealtime'
    | 'employerWelcome'
    | 'examPassedUpsell'
    | 'examRetakingDiscount'
    | 'expertsPromoNoti'
    | 'firstEarning'
    | 'freeMarketServiceExpired'
    | 'freeMarketServiceIsExpired'
    | 'generic'
    | 'hostingRequest'
    | 'hostingShare'
    | 'inviteFriends'
    | 'invoiceAcceptedPartial'
    | 'invoicePartialPaid'
    | 'localJobsActivation'
    | 'negotiatedProjectStatusChange'
    | 'pmb'
    | 'pmb_public'
    | 'prehireProjectCreated'
    | 'projectStatusChange'
    | 'promoteContest'
    | 'removeUserFromDirectory'
    | 'reviewPending'
    | 'reviewposted'
    | 'secondBidPlusTrialUpsell'
    | 'sellFreeMarketService'
    | 'serviceLimitReached'
    | 'serviceProjectEndedBuyer'
    | 'serviceProjectEndedSeller'
    | 'serviceProjectPostedBuyer' // possible union with 'serviceProjectPostedSeller'?
    | 'serviceSellerPromotion'
    | 'serviceSubmitted'
    | 'showcasePromoNoti'
    | 'sitesBidCreated'
    | 'specialLoyaltyDiscount'
    | 'supportGetHelpNewsfeed'
    | 'tombstoneNoti'
    | 'tombstoneNotiEmployer'
    | 'tombstoneNotiFreelancer'
    | 'upsellAssistedUpgrade'
    | 'upsellFeatureUpgrade'
    | 'upsellIntro'
    | 'userSideAction'
    | 'userSideActionFeedback'
    | 'yahooMailAlert';
  readonly data: any;
}

/**
 * Full list of supported notifications events.
 */
export type WebsocketNotificationsEvent =
  | WebscoketAddProjectCollaboratorEvent
  | WebsocketAcceptDisputeOfferEvent
  | WebsocketAcceptedEvent
  | WebsocketAcceptedProjectTitleEditRequestEvent
  | WebsocketActivateFreelancerEvent
  | WebsocketAdminForceVerifyPhoneEvent
  | WebsocketArticleCommentEvent
  | WebsocketAwardBadgeEvent
  | WebsocketAwardCreditEvent
  | WebsocketAwardEvent
  | WebsocketAwardMilestoneReminderEvent
  | WebsocketAwardReminderEvent
  | WebsocketAwardXpEvent
  | WebsocketBalanceUpdateEvent
  | WebsocketBidEvent
  | WebsocketBidHourlyCurrentCycleSummaryUpdateEvent
  | WebsocketBidretractedEvent
  | WebsocketBookmarkedProjectAwardedEvent
  | WebsocketCancelMilestoneEvent
  | WebsocketCartItemsUpdateEvent
  | WebsocketClientViewedBidEvent
  | WebsocketIpContractUpdateEvent
  | WebsocketCompleteContestReviewEvent
  | WebsocketCompleteReviewEvent
  | WebsocketContestAwardedToEmployerEvent
  | WebsocketContestAwardedToFreelancerEvent
  | WebsocketContestCommentEvent
  | WebsocketContestCompleteEvent
  | WebsocketContestCreatedEvent
  | WebsocketContestEntryBoughtToEmployerEvent
  | WebsocketContestEntryBoughtToFreelancerEvent
  | WebsocketContestEntryCommentedToFreelancerEvent
  | WebsocketContestEntryCreatedEvent
  | WebsocketContestEntryEngagedEvent
  | WebsocketContestEntryEvent
  | WebsocketContestEntryRatedEvent
  | WebsocketContestEntryRatedToFreelancerEvent
  | WebsocketContestEntryReconsideredToFreelancerEvent
  | WebsocketContestEntryRejectedToFreelancerEvent
  | WebsocketContestEvent
  | WebsocketContestExpiredEvent
  | WebsocketContestPCBEvent
  | WebsocketContestPCBNotificationEvent
  | WebsocketContestPCBNotificationFullViewEvent
  | WebsocketContestReviewPostedEvent
  | WebsocketContestUpgradedEvent
  | WebsocketCreateMilestoneEvent
  | WebsocketCustomAdminNotificationEvent
  | WebsocketDeniedEvent
  | WebsocketDeprecatedNotificationsEvent
  | WebsocketDirectTransferDoneEvent
  | WebsocketDraftContestEvent
  | WebsocketDriveFileUploadEvent
  | WebsocketEditAwardedBidAcceptedEvent
  | WebsocketEditAwardedBidCreatedEvent
  | WebsocketEditAwardedBidDeclinedEvent
  | WebsocketEditProjectCollaboratorPermissionsEvent
  | WebsocketEmailChangeEvent
  | WebsocketSecurePhoneUpdatedEvent
  | WebsocketEmailChangeConfirmedEvent
  | WebsocketEmailVerifiedEvent
  | WebsocketEscalateDisputeEvent
  | WebsocketFailingProjectEvent
  | WebsocketGenerateAutomaticMilestoneEvent
  | WebsocketGiveGetChildPartialMilestoneRelease
  | WebsocketGiveGetChildReceivedSignUpBonusEvent
  | WebsocketGiveGetChildSignUpEvent
  | WebsocketGiveGetParentBonusEvent
  | WebsocketGroupChatEvent
  | WebsocketHighLtvAnnualDiscountEvent
  | WebsocketHireMeEvent
  | WebsocketHourlyContractUpdateEvent
  | WebsocketInviteToContestEvent
  | WebsocketInviteUserBidEvent
  | WebsocketInvoiceFeedbackEvent
  | WebsocketInvoicePaidEvent
  | WebsocketInvoiceRequestedChangeEvent
  | WebsocketInvoiceRequestedEvent
  | WebsocketInvoiceSummaryUpdateEvent
  | WebsocketInvoiceUpdatedEvent
  | WebsocketInvoiceWithdrawnEvent
  | WebsocketIPAgreementUpgradeEvent
  | WebsocketKYCEvent
  | WebsocketLevelUpEvent
  | WebsocketLocalJobPostedEvent
  | WebsocketLogoutEvent
  | WebsocketMakeDisputeOfferEvent
  | WebsocketMilestoneStatusUpdateEvent
  | WebsocketNDASignedEvent
  | WebsocketNDAUpgradeEvent
  | WebsocketNewDisputeEvent
  | WebsocketNotifyFollowerEvent
  | WebsocketNPSSubmittedEvent
  | WebsocketOpenHireMeForBiddingEvent
  | WebsocketPendingFundsEvent
  | WebsocketPostDraftProjectEvent
  | WebsocketPostOnBehalfProjectEvent
  | WebsocketPrizeDispersedEmployerEvent
  | WebsocketPrizeDispersedFreelancerEvent
  | WebsocketProfileNameUpdateEvent
  | WebsocketProfilePictureUpdateEvent
  | WebsocketProjectBiddingClosingSoonEvent
  | WebsocketProjectCompletedEvent
  | WebsocketProjectEmailVerificationRequiredEvent
  | WebsocketProjectEvent
  | WebsocketProjectHireMeExpiredEvent
  | WebsocketProjectInviteToBid
  | WebsocketProjectSearchActiveEvent
  | WebsocketProjectSharedWithYouEvent
  | WebsocketProjectStatusChangeEvent
  | WebsocketProjectTipSentEvent
  | WebsocketProjectTitleEditRequestEvent
  | WebsocketQuickHireProjectEvent
  | WebsocketRecruiterProjectEvent
  | WebsocketRejectedEvent
  | WebsocketRejectedProjectTitleEditRequestEvent
  | WebsocketRejectOnBehalfProjectEvent
  | WebsocketRejectMilestoneCancellationEvent
  | WebsocketReleaseMilestoneEvent
  | WebsocketReleasePartMilestoneEvent
  | WebsocketRemindUseBidsEvent
  | WebsocketRenamedProjectTitleEvent
  | WebsocketResourceEvent
  | WebsocketRequestCancelMilestoneEvent
  | WebsocketRequestEndProjectEvent
  | WebsocketRequestMilestoneEvent
  | WebsocketRequestToReleaseEvent
  | WebsocketReviewActivateEvent
  | WebsocketReviewPostedEvent
  | WebsocketRevokedEvent
  | WebsocketRevokeProjectCollaboratorPermissionsEvent
  | WebsocketSearchActiveContestEvent
  | WebsocketSendDisputeMessageEvent
  | WebsocketServiceApprovedEvent
  | WebsocketServiceProjectEndingSellerEvent
  | WebsocketServiceProjectPostedSellerEvent
  | WebsocketServiceRejectedEvent
  | WebsocketServiceStatusChangedEvent
  | WebsocketShowcaseSourceApprovalEvent
  | WebsocketSignUpFreeTrialUpsellEvent
  | WebsocketSubmitOnBehalfProjectEvent
  | WebsocketSponsorBidEvent
  | WebsocketSuggestionForFreelancerAfterReceiveReviewEvent
  | WebsocketSupportAgentEvent
  | WebsocketSupportAgentSessionEvent
  | WebsocketSupportQueueEvent
  | WebsocketTaskCreateEvent
  | WebsocketTaskUpdateEvent
  | WebsocketTimeTrackingSessionCreateEvent
  | WebsocketTimeTrackingSessionDeleteEvent
  | WebsocketTrackUpdateEvent
  | WebsocketTrackCreateEvent
  | WebsocketUnknownNotificationsEvent
  | WebsocketUpdateMilestoneEvent
  | WebsocketUpgradeToNonFreeMembershipEvent
  | WebsocketUploadFileEvent
  | WebsocketUpsellCrowdsourceFindingEvent
  | WebsocketUpsellPlusTrialEvent
  | WebsocketUserReportsActionBidRestrictionEvent
  | WebsocketUserStatusUpdateEvent
  | WebsocketVerificationRequestEvent
  | WebsocketVerificationRequestChangeEvent
  | WebsocketWelcomeEvent
  | WebsocketWinningDesignReadyForReviewEvent
  | WebsocketXpContestEvent;

// This models the data from getContestData() in event_notify.php
export type WebsocketContestData = TimeSeconds & {
  readonly id: string | number;
  readonly actionText: any;
  readonly appended_descr: string;
  readonly buyer: string;
  readonly buyerUrl: string;
  readonly imgUrl: string;
  readonly isTest: boolean;
  readonly jobs: readonly number[] | readonly string[];
  readonly jobString: string;
  readonly linkUrl: string;
  readonly title: string;
  readonly userId: string;
  readonly userName: string;
};

// This models the data from pack_project() in event_notify.php
export interface WebsocketProjectData {
  readonly id: number;
  readonly actionText: string;
  readonly appended_descr: string;
  readonly imgUrl: string;
  readonly isTest: boolean;
  readonly jobs: readonly string[] | null;
  readonly jobString: string;
  readonly linkUrl: string;
  readonly recruiter: boolean;
  readonly text: string;
  readonly title: string;
  readonly userId: string | number;
  readonly userName: string;
}

interface WebsocketServiceData {
  readonly id: number;
  readonly service_id: string | number;
  readonly service_name: string;
  readonly service_url: string;
  readonly service_duration: string;
  readonly service_cost: string | number;
  readonly currency_sign: string;
  readonly currency_code: string;
  readonly service_img: string; // A URL
}

export interface WebsocketArticleCommentEvent
  extends BaseServerNotificationsData {
  readonly type: 'articleCommentReceived';
  readonly data: {
    readonly article_author: string | number; // The ID of author
    readonly article_name: string;
    readonly article_type: string; // Some enum?
    readonly comment_by: string | number;
    readonly comment_date:
      | string
      | {
          readonly timezone_type: number;
          readonly date: string;
          readonly timezone: string;
        };
    readonly comment_id: string | number;
    readonly comment: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName: string;
    readonly user: string;
  };
}

export interface WebsocketSponsorBidEvent extends BaseServerNotificationsData {
  readonly type: 'sponsor_bid';
  readonly data: {
    readonly id: string | number; // project id
    readonly object: {
      readonly bid: {
        readonly id: string | number;
        // readonly awardTime: string | number | null ,
        // readonly award_status: string | false,
        // readonly buyer_comment: string | null,
        // readonly buyer_rating: number | null,
        // readonly complete_status: string | null,
        // readonly descr: string | null,
        // readonly firstsubmitdate: string,
        // readonly hidden: boolean,
        // readonly hide_reason: null,
        // readonly highlight: boolean,
        // readonly loaded: boolean,
        // readonly milestone_percentage: string | number,
        // readonly notifylowerbids: boolean,
        // readonly paid_status: string | null,
        // readonly period: string | number,
        // readonly project_id: number,
        // readonly retracted: boolean,
        // readonly seller: null, // TODO: fix type
        // readonly sellers_notified: boolean,
        // readonly sponsored: null, // TODO: fix type
        // readonly submitdate: string
        readonly sum: string | number; // sponsor amount
        readonly users_id: number;
      };
    };
  };
}

export interface WebsocketAwardEvent extends BaseServerNotificationsData {
  readonly type: 'award';
  readonly state?: string;
  readonly data: {
    readonly acceptByTime?: number;
    readonly actionText?: string;
    readonly apiMessage: {
      readonly bid: {
        readonly amount: number;
        readonly award_status: BidAwardStatusApi.PENDING;
        readonly bidder_id: number;
        readonly id: number;
        readonly period: number;
      };
      readonly project: {
        readonly currency: { readonly code: string; readonly sign: string };
        readonly id: number;
        readonly owner_id: number;
        readonly title: string;
        readonly type: 'fixed' | 'hourly';
      };
      readonly users: { readonly [id: string]: { readonly username: string } };
    };
    readonly appended_descr: string;
    readonly awardeeId?: number;
    readonly awardeeName?: string;
    readonly bid: {
      readonly bidAmount: string;
      readonly bidDescr: string | null;
      readonly bidId: number;
      readonly bidPeriod: string;
      readonly id: number;
    };
    readonly categoryName?: string;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly id: number;
    readonly imgUrl?: string;
    readonly isTest?: boolean;
    readonly jobs: readonly string[];
    readonly jobString: string;
    readonly linkUrl?: string;
    readonly publicName: string;
    readonly projIsHourly: boolean;
    readonly text?: string;
    readonly title: string;
    readonly userId: number;
    readonly userName: string;
  };
}

export interface WebsocketAwardBadgeEvent extends BaseServerNotificationsData {
  readonly type: 'awardBadge';
  readonly data: {
    readonly descr: string;
    readonly id: number;
    readonly linkUrl: string;
    readonly name: string;
  };
}

export interface WebsocketAwardCreditEvent extends BaseServerNotificationsData {
  readonly type: 'awardCredit';
  readonly data: {
    readonly amount: string | number;
    readonly id: string | number;
    readonly linkUrl: string;
  };
}

export interface WebsocketAwardReminderEvent
  extends BaseServerNotificationsData {
  readonly type: 'awardReminder';
  readonly data: {
    // Note, this doesn't actually have an `id`.
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly userName: string;
    readonly bid: { readonly id: string | number };
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardMilestoneReminderEvent
  extends BaseServerNotificationsData {
  readonly type: 'awardMilestoneReminder';
  readonly data: {
    readonly appended_descr: string;
    readonly currencycode: string;
    readonly currencyid: string | number;
    readonly currencysign: string;
    readonly id: string | number;
    readonly imgUrl: string;
    // isAutomaticPayments: boolean;
    readonly linkUrl: string;
    readonly projIsHourly: boolean;
    // TODO: T243830 Frontend should be able to use isInsource straight from the backend. Currently we're doing in the transformers as a temporary measure for MVP.
    readonly projIsToken: boolean;
    readonly publicName?: string;
    readonly title: string;
    readonly userId: string | number;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardXpEvent extends BaseServerNotificationsData {
  readonly type: 'awardXp';
  readonly data: {
    readonly amount: string | number;
    readonly descr: string;
    readonly game: 'xp';
    readonly id: string | number;
    readonly linkUrl: string;
  };
}

export interface WebsocketBalanceUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'balanceUpdate';
  readonly data: {
    readonly balances: readonly BalanceApi[];
    readonly primary_currency: CurrencyApi;
  };
}

interface WebsocketBidEventProjectCount {
  readonly all: number;
  readonly incomplete_reviews: number;
  readonly complete: number;
  readonly completion_rate: number;
  readonly reviews: number;
  readonly earnings: number;
  readonly incomplete: number;
}

interface WebsocketBidEventReputation {
  readonly com: number;
  readonly exp: number;
  readonly hag: number;
  readonly onb: number;
  readonly ont: number;
  readonly overall: number;
  readonly pos: number;
  readonly pro: number;
  readonly qua: number;
}

interface WebsocketBidEventSellerRating {
  readonly count: number;
  readonly projectCount: {
    readonly last12months: WebsocketBidEventProjectCount;
    readonly last3months: WebsocketBidEventProjectCount;
    readonly entire_history: WebsocketBidEventProjectCount;
  };
  readonly completionRate: number;
  readonly avg: number;
  readonly reputation: {
    readonly last12months: WebsocketBidEventReputation;
    readonly last3months: WebsocketBidEventReputation;
    readonly entire_history: WebsocketBidEventReputation;
  };
}

export interface WebsocketBidretractedEvent
  extends BaseServerNotificationsData {
  readonly type: 'bidretracted';
  readonly data: {
    readonly bid_amount: number;
    readonly bid_id: number;
    readonly project_id: number;
    readonly seller_id: number;
  };
}

export interface WebsocketBidEvent extends BaseServerNotificationsData {
  readonly type: 'bid' | 'bid_updated' | 'bid_self';
  readonly data: {
    readonly accountId?: number;
    readonly amount: string;
    readonly award?: string;
    readonly bidAvg?: string | number | false;
    readonly bidCount?: number;
    readonly bidHidden: boolean;
    readonly bidNo?: string | false | null;
    readonly buyerFeePerDollar?: string;
    readonly buyerMinProjectFee?: string;
    readonly categoryName?: string;
    readonly currency: string;
    readonly currencyCode: string;
    readonly currencyId: string;
    readonly descr: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly initMilestone?: number;
    readonly linkUrl?: string;
    readonly period: string;
    readonly pmb: any;
    readonly projectId: string | number;
    readonly projIsFullTime: boolean;
    readonly projIsHourly: boolean;
    readonly projIsToken: boolean;
    readonly projName: string;
    readonly publicName?: string;
    readonly showAwardFee: boolean;
    readonly text?: string;
    readonly title: string;
    readonly userId: string | number;
    readonly userName: string;
    readonly userBalances?: {
      readonly all:
        | readonly {
            readonly currency: {
              readonly code: string;
              readonly country: string;
              readonly exchangerate: string;
              readonly id: string;
              readonly is_external?: string;
              readonly name: string;
              readonly seq: string;
              readonly sign: string;
            };
            readonly balance: string;
          }[]
        | null;
      readonly relevant: number;
    } | null;
    readonly bid: {
      readonly award_score?: number;
      readonly completed?: boolean;
      readonly completion_score?: number;
      readonly descr_html?: string;
      readonly descr_short_html?: string;
      readonly descr_short?: string;
      readonly descr?: string | null;
      // expert_guarantee?: ExpertGuaranteeApi | null; // These fields in ExpertGuaranteeApi can be strings in the actualy websocket
      readonly highlighted?: boolean | null;
      readonly id: string | number;
      readonly isShortlisted?: boolean | null;
      readonly milestone_percentage?: string | number;
      readonly no?: string | false | null;
      readonly paid_status?: false | null;
      readonly period?: string;
      readonly project_id?: string | number;
      readonly r2?: number;
      readonly ranking?: number;
      readonly score?: number;
      readonly sponsored?: boolean | null;
      readonly submitdate_f?: string;
      readonly submitdate_f2?: string;
      readonly submitdate_ts?: number;
      readonly submitdate?: string;
      readonly sum?: string;
      readonly users_id?: number;
      readonly user?: {
        readonly address: {
          readonly country: string | null;
          readonly code: string;
          readonly city: string | null;
        };
        readonly company: string | null;
        readonly currency: number;
        readonly earnings: string | number;
        readonly flag_class: string;
        readonly flag_name: string | null;
        readonly flag: { readonly name: string | null; readonly icon: string };
        readonly gold: number;
        readonly hourlyrate: number | null;
        readonly id: number;
        readonly insignia: readonly {
          readonly name: string;
          readonly title: string;
          readonly examEnabled: string | null;
          readonly examId: string | null;
          readonly id: string;
          readonly description: string;
        }[];
        readonly isPortfolioCreated: boolean;
        readonly jobs: readonly string[];
        readonly logo_Url?: string;
        readonly normalMemberclassLogo: string;
        readonly online?: string | null | false;
        readonly profile_logo_Url?: string;
        readonly provider_rating: {
          readonly count: number;
          readonly avg: string | number;
        };
        readonly publicName?: string;
        readonly rating: {
          readonly count: number;
          readonly avg: string | number;
        };
        readonly reg_date: string;
        readonly reputation_rate: number;
        readonly timezone_info?: TimezoneApi; // This will be missing in old newsfeed objects.
        readonly tooltipMemberclassLogo: string;
        readonly Url?: string;
        readonly username: string;
        readonly seller_rating: WebsocketBidEventSellerRating;
      };
    };
  };
}

interface BidWebsocketApiMessage {
  readonly bid: {
    readonly amount?: number;
    readonly award_status: BidAwardStatusApi;
    readonly complete_status: BidCompleteStatusApi;
    readonly bidder_id: number;
    readonly id: number;
    readonly period: number;
  };
  readonly project: {
    readonly currency?: {
      readonly code: string;
      readonly id: number;
      readonly sign: string;
    };
    readonly id: number;
    readonly owner_id: number;
    readonly title?: string;
  };
  readonly users?: {
    readonly [index: string]: { readonly username: string };
  };
}

export interface WebsocketAcceptedEvent extends BaseServerNotificationsData {
  readonly type: 'accepted';
  readonly data: {
    readonly apiMessage: BidWebsocketApiMessage;
    readonly bid: {
      readonly retracted: boolean;
      readonly descr: string | null;
      readonly id: number;
    };
    readonly id: number;
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly isAutomaticPayments: boolean;
    readonly isHourly: boolean;
    readonly isToken: boolean;
    readonly milestoneCreated: boolean;
    readonly name: string;
    readonly publicName: string;
    readonly seoUrl: string | number | null;
    readonly userName: string;
    readonly userId: number;
    readonly awardeeName: string;
    readonly awardeeId: number;
    readonly bidId: number;
    readonly milestoneIds: readonly number[];
    readonly projectType: NotificationProjectType;
  };
}

export interface WebsocketRevokedEvent extends BaseServerNotificationsData {
  readonly type: 'revoked';
  readonly data: {
    readonly apiMessage: {
      readonly project: {
        readonly id: number;
        readonly owner_id: number;
        readonly title: string;
        readonly seo_url: string;
        readonly hireme: boolean;
      };
      readonly bid: {
        readonly id: number;
        readonly bidder_id: number;
        readonly award_status: BidAwardStatusApi;
      };
    };
    readonly awardeeId: number;
    readonly bidId: string | number;
    readonly imgUrl: string;
    readonly projectId: number;
  };
}

export interface WebsocketBookmarkedProjectAwardedEvent
  extends BaseServerNotificationsData {
  readonly type: 'bookmarkedProjectAwarded';
  readonly data: {
    readonly bidAmount: string;
    readonly currencyCode: string;
    readonly projectId: number;
    readonly projectSeoUrl: string;
    readonly projIsHourly: boolean;
    readonly publicName: string;
    readonly title: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface WebsocketCancelMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'cancelMilestone';
  readonly data: {
    readonly id: number;
    readonly tranId: string | number;
  };
}

export interface WebsocketProjectCompletedEvent
  extends BaseServerNotificationsData {
  readonly type: 'completed';
  readonly state?: string;
  readonly data: {
    readonly amount: string | number;
    readonly categoryName: string;
    readonly completeStatus: string; // TODO: T37445 Make this a string enum
    readonly currencycode: string;
    readonly currencyid: string | number;
    readonly currencysign: string;
    readonly employerId: string | number;
    readonly employerPublicName?: string;
    readonly employerUsername: string;
    readonly expiredTimestamp: string | null;
    readonly freelancerId: string | number;
    readonly freelancerPublicName?: string;
    readonly freelancerUsername: string;
    readonly id: string | number;
    readonly img_e: { readonly profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    readonly img_f: { readonly profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    readonly name: string;
    // projIsHourly: boolean;
    readonly selectionId: string | number;
    readonly seoUrl: string | number | null;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly submitDate: string;
    readonly bidId?: string | number | null;
  };
}

export interface WebsocketContestEvent extends BaseServerNotificationsData {
  readonly type: 'contest';
  readonly data: WebsocketContestData & {
    readonly currency?: string;
    readonly currencyCode: string;
    readonly exchangerate: string;
    readonly extended: boolean;
    readonly featured: boolean;
    readonly free_bid_until?: number;
    readonly highlight: any;
    readonly maxbudget?: string | number;
    readonly private: boolean;
    readonly sealed: boolean;
    readonly submitDate: string;
  };
}

export interface WebsocketSearchActiveContestEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestSearchActive';
  readonly data: WebsocketContestData & {
    readonly currency: Currency;
    readonly description: string;
    readonly entryCount: number;
    readonly ownerId: number;
    readonly prize: number;
    readonly seoUrl: string;
    readonly skillIds: readonly number[];
    readonly skills: readonly JobApi[];
    readonly status: ContestStatusApi;
    readonly timeSubmitted: number;
    readonly type: ContestTypeApi;
    readonly upgradeNames: readonly UpgradeType[];
    readonly draft?: boolean;
    readonly duration?: number;
    readonly enterpriseId?: number;
    readonly timeEnded?: number;
    readonly timePosted?: number;
    readonly upgrades?: ContestUpgrade;
  };
}

export interface WebsocketContestAwardedToEmployerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestAwardedToEmployer';
  readonly data: {
    readonly contestName: string;
    readonly contestSEOURL?: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly entry_id: string;
    readonly entry_number: string;
    readonly entryCount: string;
    readonly freelancerCount: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly isBought: boolean;
    readonly linkUrl: string;
    readonly prize: string | number;
    readonly publicName?: string;
    readonly thumb: string;
    readonly userId: number;
    readonly userName: string;
  };
}

export interface WebsocketContestAwardedToFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestAwardedToFreelancer';
  readonly data: {
    readonly contestName: string;
    readonly contestSEOURL: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly entry_id: string | number;
    readonly entry_number: string | number;
    readonly entryCount: string | number;
    readonly freelancerCount: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly prize: string | number;
    readonly publicName?: string;
    readonly userId: string | number;
    readonly userName: string;
    readonly thumb: string;
  };
}

export interface WebsocketContestEntryBoughtToEmployerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryBoughtToEmployer';
  readonly data: {
    readonly contestName: string;
    readonly entry_id: string | number;
    readonly entry_number: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly userName: string;
  };
}

export interface WebsocketContestEntryBoughtToFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryBoughtToFreelancer';
  readonly data: {
    readonly contestName: string;
    readonly entry_id: string | number;
    readonly entry_number: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly userName: string;
  };
}

export interface WebsocketContestEntryEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntry';
  readonly data: {
    readonly contestHolder: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly firstUser?: string;
    readonly firstUserPublicName?: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string | null;
    readonly secondUserPublicName?: string | null;
    readonly userId: string | number;
  };
}

export interface WebsocketContestEntryRatedToFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryRatedToFreelancer';
  readonly data: {
    readonly action: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly employerId: string;
    readonly entriesUrl: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: string;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface WebsocketContestEntryRejectedToFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryRejectedToFreelancer';
  readonly data: {
    readonly action: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly employerId: string;
    readonly entriesUrl: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: string;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface WebsocketContestEntryReconsideredToFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryReconsideredToFreelancer';
  readonly data: {
    readonly action: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly employerId: string;
    readonly entriesUrl: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: string;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface WebsocketContestEntryCommentedToFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryCommentedToFreelancer';
  readonly data: {
    readonly contestId: string;
    readonly contestName: string;
    readonly entryNumber: string;
    readonly id: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketContestExpiredEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestExpired';
  readonly data: {
    readonly contest_end_date: number;
    readonly contest_name: string;
    readonly contest_url: string;
    readonly isExtended: '0' | '1' | boolean;
  };
}

export interface WebsocketContestPCBNotificationEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestPCBNotification';
  readonly data: {
    readonly contestHolder: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly firstUser?: string;
    readonly firstUserPublicName?: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string | null;
    readonly secondUserPublicName?: string | null;
    readonly userId: number;
    readonly userCount: number;
  };
}

export interface WebsocketContestPCBNotificationFullViewEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestPCBNotificationFullView';
  readonly data: {
    readonly contestHolder: string | number;
    readonly contestName: string;
    readonly entryNumber: string | number | null;
    readonly firstUser: string;
    readonly firstUserPublicName?: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string | null;
    readonly secondUserPublicName?: string | null;
    readonly userId: string | number;
    readonly userCount: number;
  };
}

export interface WebsocketContestUpgradedEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestUpgraded';
  readonly data: {
    readonly contest_id: number;
    readonly new_upgrade: ContestUpgradeApi;
    readonly seo_url: string;
  };
}

export interface WebsocketMilestone {
  readonly transaction_id?: number | boolean;
  readonly project_owner_id?: string;
  readonly bidder_id?: string;
  readonly amount?: number;
  readonly reason?: MilestoneReasonApi | 'partly_payment';
  readonly other_reason?: string;
  readonly project_id?: string | number;
  readonly bid_id?: number;
  readonly currency?: CurrencyAjax;
  readonly is_from_prepaid?: boolean;
  readonly status?: MilestoneStatusApi;
  readonly dispute_id: string | null;
  readonly time_created?: string | boolean;
  readonly is_initial_payment?: boolean;
}

export interface WebsocketCreateMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'createMilestone';
  readonly data: WebsocketProjectData & {
    readonly account_id: string | number | null;
    readonly amount: string | number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly project_id: string | number | null;
    readonly publicName?: string;
    readonly shortDescr: string;
    readonly transId: string | number | null | false;
    /* From NewEscrowHandler::save */
    readonly newMilestone: WebsocketMilestone; // Might be undefined?
    // readonly bidId: string | number;
    readonly created_milestone_request_id: string | number | null | false;
  };
}

export interface WebsocketDeniedEvent extends BaseServerNotificationsData {
  readonly type: 'denyed';
  readonly data: {
    readonly apiMessage: {
      readonly project: {
        readonly id: number;
        readonly title: string;
        readonly owner_id: number;
        readonly currency: {
          readonly sign: string;
          readonly code: string;
        };
      };
      readonly bid: {
        readonly bidder_id: number;
        readonly award_status: 'rejected';
      };
      readonly users: {
        readonly [userId: string]: { readonly username: string };
      };
    };
    readonly autoRepostProjectId?: string | number;
    readonly autoRepostProjectName?: string;
    readonly bidId: string | number;
    readonly currencycode: string;
    readonly currencyId: string | number;
    readonly currencysign: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly jobsURL: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly projectOfferId?: number;
    readonly projectType: NotificationProjectType;
    readonly projIsHourly: any;
    readonly projIsToken: boolean;
    readonly publicName?: string;
    readonly reason: string;
    readonly seoUrl: string | number | null;
    readonly userId: string | number;
    readonly userName: string;
    readonly poolIds: readonly number[];
  };
}

export interface WebsocketDraftContestEvent
  extends BaseServerNotificationsData {
  readonly type: 'draftContest';
  readonly data: {
    readonly contestEditUrl?: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly contestUrl: string;
    readonly imgUrl: string;
    readonly isPublished: boolean;
  };
}

export interface WebsocketDriveFileUploadEvent extends BaseServerData {
  readonly type: 'driveFileUpload';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly drive_file: DriveFileApi;
    readonly context: DriveFileContext;
  };
}

export interface WebsocketFailingProjectEvent
  extends BaseServerNotificationsData {
  readonly type: 'failingProject';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly nonpublic: boolean;
      readonly projectFreeBid: boolean;
      readonly projIsHourly: boolean;
      readonly publicName?: string;
      readonly submitDate: string;
      readonly xpValue: number;
    };
}

export interface WebsocketGiveGetChildReceivedSignUpBonusEvent
  extends BaseServerNotificationsData {
  readonly type: 'giveGetChildReceivedSignupBonus';
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly bonus: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketGiveGetChildSignUpEvent
  extends BaseServerNotificationsData {
  readonly type: 'giveGetChildSignUp';
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly childName: string;
    readonly bonus: number;
    readonly bonusRequirement: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketGiveGetChildPartialMilestoneRelease
  extends BaseServerNotificationsData {
  readonly type: 'giveGetChildPartialMilestoneRelease';
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly childName: string;
    readonly bonusRequirement: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketGiveGetParentBonusEvent
  extends BaseServerNotificationsData {
  readonly type: 'giveGetParentBonus';
  readonly data: {
    readonly parentId: number;
    readonly bonus: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketHighLtvAnnualDiscountEvent
  extends BaseServerNotificationsData {
  readonly type: 'highLtvAnnualDiscount';
  readonly data: {
    readonly id: string | number;
    readonly userDisplayName: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketHireMeEvent extends BaseServerNotificationsData {
  readonly type: 'hireMe';
  // NOTE: doesn't have isTest, so can't use WebsocketProjectData
  readonly state?: string;
  readonly data: {
    readonly acceptByTime?: number;
    readonly appended_descr: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly jobString: string;
    readonly linkUrl: string;
    readonly period?: string | number;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly sum: string | number;
    readonly title: string;
    readonly userId: number;
    readonly userName: string;
    // accountId: $project->getAccountId();
    // readonly bid: {
    //   readonly id: string | number;
    // };
    // readonly currency?: { readonly code: string; readonly sign: string };
    // featured: $project->isFeatured();
    // img: $img;
    // name: string;
    // seoUrl: $project->getSeoUrl();
  };
}

export interface WebsocketInviteUserBidEvent
  extends BaseServerNotificationsData {
  readonly type: 'inviteUserBid';
  readonly data: WebsocketProjectData & {
    readonly currencycode: string;
    readonly currencysign: string;
    readonly inviteeId: string | number;
    readonly minBudget?: number; // The only place with camelCase min/maxBudget
    readonly maxBudget?: number | false; // TODO: use BudgetMinMaxBackend if possible (requires backend changes)
    readonly inviteeName: string;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
  };
}

export interface WebsocketInviteToContestEvent
  extends BaseServerNotificationsData {
  readonly type: 'inviteToContest';
  readonly data: {
    readonly buyer: string | number; // The ID
    readonly buyerName: string;
    readonly buyerPublicName?: string;
    readonly buyerUrl: string;
    readonly contest_recommended_skills: string;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly description: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly prize: string | number;
    readonly sellerId: string | number;
    readonly sellerName: string;
    readonly sellerPublicName?: string;
    readonly title: string;
    readonly type: string | null;
  };
}

export interface WebsocketInvoiceUpdatedEvent
  extends BaseServerNotificationsData {
  readonly type: 'invoiceUpdated';
  readonly data: {
    readonly projectId: number;
    readonly bidId: number;
    readonly buyerId: number;
    readonly sellerId: number;
    readonly invoiceId: number;
    readonly status: InvoiceStatusApi;
    readonly isExternal: boolean;
    readonly isAutomatic: boolean;
    readonly currency: CurrencyApi;
    readonly timeRaised: number;
    readonly periodStart: number;
    readonly periodEnd: number;
    readonly milestoneId: number | null;
    readonly totalHours: number;
    readonly totalHoursAmount: number;
    readonly totalResultAmount: number;
    readonly totalOtherAmount: number;
    readonly totalMilestoneAmount: number;
    readonly latestUnreviewedByEmployer: boolean;
  };
}

export interface WebsocketGenerateAutomaticMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'generateAutomaticMilestone';
  readonly data: WebsocketMilestone;
}

export interface WebsocketInvoiceRequestedEvent
  extends BaseServerNotificationsData {
  readonly type: 'invoiceRequested';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly dueDate: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly invoiceId: string | number;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly sellerId: string | number;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketLevelUpEvent extends BaseServerNotificationsData {
  readonly type: 'levelUp';
  readonly data: {
    readonly id: string | number;
    readonly level: string;
    readonly membership: boolean;
    readonly perks: readonly {
      readonly description: string;
      readonly item: boolean;
      readonly magnitude: number;
      readonly name?: 'BidRefreshRate' | 'item';
      readonly symbol: string;
    }[];
    readonly username: string;
    readonly linkUrl: string;
    readonly game: 'level';
  };
}

export interface WebsocketLocalJobPostedEvent
  extends BaseServerNotificationsData {
  // Note: this is actually an extension of WebsocketProjectEvent's data.
  readonly type: 'localJobPosted';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string | null;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly exchangerate: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly isLocal: true;
      readonly nonpublic: boolean;
      readonly projIsHourly: boolean;
      readonly submitDate: string;
      readonly publicName?: string;
    };
}

export interface WebsocketLogoutEvent extends BaseServerNotificationsData {
  readonly type: 'logout';
  readonly data: {};
}

export interface WebsocketNotifyFollowerEvent
  extends BaseServerNotificationsData {
  readonly type: 'notifyfollower';
  readonly data: WebsocketProjectData;
}

export interface WebsocketNPSSubmittedEvent
  extends BaseServerNotificationsData {
  readonly type: 'npsSubmitted';
  readonly data: {
    readonly netPromoterScore: {
      readonly id: number;
      readonly from_user_id: number;
      readonly type: NetPromoterScoreTypeApi;
      readonly score: number;
      readonly comment: string;
      readonly source: NetPromoterScoreSourceApi;
      readonly source_id: number;
      readonly timestamp: number | null;
      readonly to_entity_id: number;
    };
  };
}

export interface WebsocketProjectSearchActiveEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectSearchActive';
  readonly data: WebsocketProjectData & {
    readonly bid_stats: {
      readonly bid_count: number;
      readonly bid_avg?: number;
    };
    readonly currency: Currency;
    readonly deleted: boolean;
    readonly extend: boolean;
    readonly featured: boolean;
    readonly fulltime: boolean;
    readonly hideBids: boolean;
    readonly hireme: boolean;
    readonly ip_contract: boolean;
    readonly isEscrowProject: boolean;
    readonly isLocal: boolean;
    readonly jobs_details: readonly JobApi[];
    readonly language: string;
    readonly listed: boolean;
    readonly minbudget: number;
    readonly NDA: boolean;
    readonly nonpublic: boolean;
    readonly overallReputation: number;
    readonly project_management: boolean;
    readonly qualified: boolean;
    readonly reviews: number;
    readonly sealed: boolean;
    readonly state: string;
    readonly time_submitted: number;
    readonly type: ProjectTypeApi;
    readonly urgent: boolean;
    readonly bidperiod?: number;
    readonly country?: string;
    readonly enterpriseId?: number;
    readonly latitude?: number;
    readonly longitude?: number;
    readonly maxbudget?: number;
    readonly pfOnly?: boolean;
    readonly pool_ids?: readonly number[];
    readonly subStatus?: string;
  };
}
export interface WebsocketProjectEvent extends BaseServerNotificationsData {
  readonly type: 'project';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string;
      readonly currency?: string;
      readonly currencyCode: string;
      // exchangerate is not used and the back-end does not send it all the
      // time, which is fine since we should be using the currency pipe
      // which handles this.
      // readonly exchangerate: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly isLocal: boolean;
      readonly nonpublic: boolean;
      readonly projIsHourly: boolean;
      readonly submitDate: string | false;
      readonly publicName?: string;
    };
}

export interface WebsocketRecruiterProjectEvent
  extends BaseServerNotificationsData {
  readonly type: 'recruiterProject';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly exchangerate: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly isLocal: boolean;
      readonly nonpublic: boolean;
      readonly projIsHourly: boolean;
      readonly submitDate: string;
      readonly publicName?: string;
    };
}

export interface WebsocketQuickHireProjectEvent
  extends BaseServerNotificationsData {
  readonly type: 'quickHireProject';
  readonly data: WebsocketProjectData & {
    readonly bidderId: string | number;
    readonly bidderName: string;
    readonly currency?: { readonly code: string; readonly sign: string };
    // featured: boolean; // TODO: T37447 Are these boolean or something different?
    // fulltime: boolean; // TODO: T37447 Are these boolean or something different?
    // hideBids: boolean; // TODO: T37447 Are these boolean or something different?
    // nonpublic: boolean; // TODO: T37447 Are these boolean or something different?
    readonly period?: string | number;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly submitDate: string;
    readonly sum?: string | number;
  };
}

export interface WebsocketReleaseMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'releaseMilestone';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly otherReason: string;
    readonly publicName?: string;
    readonly seoUrl: string | number | null;
    readonly submitDate: string;
    readonly title: string;
    readonly userId: string | number;
    readonly userName: string;
    readonly milestoneId: string | number;
  };
}

export interface WebsocketRequestEndProjectEvent
  extends BaseServerNotificationsData {
  readonly type: 'requestEndProject';
  readonly data: {
    readonly accountId: string | number;
    readonly bidId: string | number;
    readonly categoryName: string;
    readonly dayCount: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string | number | null;
    readonly submitDate: string;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketRequestMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'requestMilestone';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencyCode: string;
    readonly currencyId: string | number;
    readonly currencySign: string;
    readonly description: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly requestId: string | number;
    readonly sellerId: string | number;
    readonly seoUrl: string | number | null;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly submitDate: string;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketRequestToReleaseEvent
  extends BaseServerNotificationsData {
  readonly type: 'requestToRelease';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly descr: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string | number | null;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly tranId: string | number;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketServiceApprovedEvent
  extends BaseServerNotificationsData {
  readonly type: 'serviceApproved';
  readonly data: WebsocketServiceData;
}

export interface WebsocketServiceProjectEndingSellerEvent
  extends BaseServerNotificationsData {
  readonly type: 'serviceProjectEndingSeller';
  readonly data: {
    readonly bid_id: string | number;
    readonly buyer_id: string | number;
    readonly buyer_name: string;
    readonly hours_remaining: number;
    readonly id: string | number;
    readonly order_start_date: string;
    readonly project_id: string | number;
    readonly project_name: string;
    readonly project_url: string;
    readonly seller_id: string | number;
    readonly seller_name: string;
    readonly service_id: string | number;
    readonly service_name: string;
  };
}

export interface WebsocketServiceProjectPostedSellerEvent
  extends BaseServerNotificationsData {
  readonly type: 'serviceProjectPostedSeller';
  readonly data: {
    readonly bid_id: string | number;
    readonly buyer_id: string | number;
    readonly buyer_img_url: string;
    readonly buyer_name: string;
    readonly buyer_public_name?: string;
    readonly currency_code: string;
    readonly currency_sign: string;
    // Not used or sent from the back-end.
    // readonly id: string | number;
    readonly order_end_date: string;
    readonly order_start_date: string;
    readonly project_cost: string | number;
    // project_duration: ???;
    readonly project_id: string | number;
    // project_milestones: ???;
    readonly project_name: string;
    readonly project_url: string;
    readonly seller_id: string | number;
    readonly seller_img_url: string;
    readonly seller_name: string;
    readonly seller_public_name: string;
    readonly service_id: string | number;
    readonly service_name: string;
    readonly service_url: string;
  };
}

export interface WebsocketServiceStatusChangedEvent
  extends BaseServerNotificationsData {
  readonly type: 'serviceStatusChanged';
  readonly data: {
    readonly id: string | number;
    // ???
  };
}

export interface WebsocketServiceRejectedEvent
  extends BaseServerNotificationsData {
  readonly type: 'serviceRejected';
  readonly data: WebsocketServiceData;
}

export interface WebsocketSignUpFreeTrialUpsellEvent
  extends BaseServerNotificationsData {
  readonly type: 'signUpFreeTrialUpsell';
  readonly data: {
    readonly id: string | number;
    readonly linkUrl: string;
    readonly trialPackageName: string;
  };
}

export interface WebsocketUpsellCrowdsourceFindingEvent
  extends BaseServerNotificationsData {
  readonly type: 'upsellCrowdsourceFinding';
  readonly data: {
    readonly id: string | number;
    readonly project_name: string;
  };
}

export interface WebsocketXpContestEvent extends BaseServerNotificationsData {
  readonly type: 'xpContest';
  readonly data: WebsocketContestData & {
    readonly currency: string;
    readonly currencyCode: string;
    readonly maxbudget: string | number;
  };
}

export interface WebsocketTaskCreateEvent extends BaseServerNotificationsData {
  readonly type: 'tasklistCreateV1';
  readonly data: TaskApi & {
    readonly linkUrl: string;
    readonly imgUrl: string;
  };
}

type EnqueueSupportQueueEvent = AgentSessionsGetResultApi & {
  readonly queueEventType: QueueEventType.ENQUEUE;
};

type ReasignSupportQueueEvent = AgentSessionsGetResultApi & {
  readonly queueEventType: QueueEventType.REASSIGN;
};

// Unsupported legacy support queue events
type LegacySupportQueueEvent = any & {
  readonly queueEventType: QueueEventType.AVAILABILITY;
};

export interface WebsocketSupportQueueEvent
  extends BaseServerNotificationsData {
  readonly type: 'supportQueueEvent';
  readonly data:
    | EnqueueSupportQueueEvent
    | ReasignSupportQueueEvent
    | LegacySupportQueueEvent;
}

export interface WebsocketSupportAgentEvent
  extends BaseServerNotificationsData {
  readonly type: 'supportAgent';
  readonly data: {
    readonly type: 'available' | 'unavailable' | 'away';
    readonly agent: AgentApi;
  };
}

export interface WebsocketSupportAgentSessionEvent
  extends BaseServerNotificationsData {
  readonly type: 'supportAgentSession';
  readonly data: {
    readonly type: 'created' | 'assigned' | 'resolved' | 'starred';
    readonly agentSession: AgentSessionApi;
    readonly session: SessionApi;
    readonly agent?: AgentApi;
  };
}

export interface WebsocketTaskUpdateEvent extends BaseServerNotificationsData {
  readonly type: 'tasklistUpdateV1';
  readonly data: TaskApi;
}

export interface WebsocketContestEntryRatedEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryRated';
  readonly data: {
    readonly contestId: string | number;
    readonly contestName: string;
    readonly contestSeoUrl: string;
    readonly employerImg?: { readonly logo_url?: string | boolean };
    readonly employerName: string;
    readonly employerPublicName: string;
    readonly entryId: string | number;
    readonly entryNumber: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly rating: string;
    readonly time: number;
    // TODO: T37457 There's more in event_notify.php
    // readonly entryName: string;
    // TODO: Enum -> 'Withdrawn', 'Won', 'Eliminated', 'Active'
    // readonly status: string;
    // readonly employerId: string;
    // readonly entriesUrl: string;
    // readonly entryS3path: string;
    // readonly freelancerId: string;
    // readonly img: string;
    // readonly userId: string;
    // readonly userName: string;
  };
}

export interface WebsocketReviewPostedEvent
  extends BaseServerNotificationsData {
  readonly type: 'reviewpostednew';
  readonly state?: string;
  readonly data: {
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly id: number; // Project id
    readonly invoice_id?: number;
    readonly name: string; // Project name
    readonly projIsHourly: boolean;
    readonly publicName: string; // Freelancer public name
    readonly seoUrl: string | number | null; // Project url
    readonly userId: number; // User who wrote review
    readonly userName: string; // Freelancer username
    // TODO: T37457 There's more in event_notify.php
    // readonly accountId: number;
    // readonly amount: string;
    // readonly currencycode: string;
    // readonly currencyid: string;
    // readonly currencysign: string;
    // readonly expiredTimestamp: string;
    // readonly fromUserId: number;
  };
}

interface WebsocketMembershipBenefit {
  readonly benefit_value: number;
  // TODO: T37457 There's more in event_notify.php
  //   readonly benefit: {
  //     readonly display_name: string;
  //     readonly duplicate_rule: number;
  //     readonly id: number;
  //     readonly internal_name: string;
  //     readonly value: number;
  //   };

  //   readonly id: number;
  //   readonly package_id: number;
}

export interface WebsocketUpsellPlusTrialEvent
  extends BaseServerNotificationsData {
  readonly type: 'upsellPlusTrial';
  readonly data: {
    // TODO: T37457 There's more in event_notify.php
    // readonly claim_url: string;
    readonly currency_code: string;
    readonly currency_sign: string;
    readonly extendPrice: string;
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly isBoth: boolean;
    readonly isEmployer: boolean;
    readonly isFreelancer: boolean;
    readonly price: number;
    readonly taxName: string;
    readonly trialBenefits: {
      readonly bids_limit: WebsocketMembershipBenefit;
      readonly offsite_invoice: WebsocketMembershipBenefit;
      readonly skills_limit: WebsocketMembershipBenefit;
      // readonly bid_on_premium_projects: WebsocketMembershipBenefit;
      // readonly bids_for_reviews: WebsocketMembershipBenefit;
      // readonly buyer: WebsocketMembershipBenefit;
      // readonly buyer_hour: WebsocketMembershipBenefit;
      // readonly contest_entry_sold: WebsocketMembershipBenefit;
      // readonly contest_prize: WebsocketMembershipBenefit;
      // readonly cover_photo: WebsocketMembershipBenefit;
      // readonly employer_following: WebsocketMembershipBenefit;
      // readonly escrow_other: WebsocketMembershipBenefit;
      // readonly free_exams: WebsocketMembershipBenefit;
      // readonly free_extend: WebsocketMembershipBenefit;
      // readonly free_hidden_bids: WebsocketMembershipBenefit;
      // readonly free_highlighted_contest_entries: WebsocketMembershipBenefit;
      // readonly free_priority_projects: WebsocketMembershipBenefit;
      // readonly free_sealed_contest_entries: WebsocketMembershipBenefit;
      // readonly freelancer_reward: WebsocketMembershipBenefit;
      // readonly initiate_chat: WebsocketMembershipBenefit;
      // readonly preferred_freelancer: WebsocketMembershipBenefit;
      // readonly premium_project_review_minimum: WebsocketMembershipBenefit;
      // readonly project_watching: WebsocketMembershipBenefit;
      // readonly same_day_withdrawal: WebsocketMembershipBenefit;
      // readonly seller: WebsocketMembershipBenefit;
      // readonly seller_hour: WebsocketMembershipBenefit;
      // readonly seller_hour_pfp: WebsocketMembershipBenefit;
      // readonly seller_pfp: WebsocketMembershipBenefit;
      // readonly seller_rewards: WebsocketMembershipBenefit;
      // readonly sender_escrow_other: WebsocketMembershipBenefit;
      // readonly sender_transfer_other: WebsocketMembershipBenefit;
      // readonly service_posting_limit: WebsocketMembershipBenefit;
      // readonly skills_change_limit: WebsocketMembershipBenefit;
      // readonly transfer_other: WebsocketMembershipBenefit;
      // readonly transfer_tip: WebsocketMembershipBenefit;
    };
    readonly trialPackage: {
      readonly package_id: number;
      readonly package?: {
        readonly upper_name: string;
      };
      readonly pkg?: {
        readonly upper_name: string;
        // readonly benefits: {
        //   readonly benefit: {
        //     readonly display_name: string;
        //     readonly duplicate_rule: number;
        //     readonly id: number;
        //     readonly internal_name: string;
        //     readonly value: number;
        //   };
        //   readonly benefit_value: number;
        //   readonly id: number;
        //   readonly package_id: number;
        // };
        // readonly category_id: number;
        // readonly display_name: string;
        // readonly enabled: boolean;
        // readonly id: number;
        // readonly internal_name: string;
        // readonly order: number;
        // readonly prices: {
        //   readonly amount: number;
        //   readonly cerrency_id: number;
        //   readonly contract_quantity: number;
        //   readonly country: string;
        //   readonly currency_id: number;
        //   readonly duration: {
        //     readonly cycle: number;
        //     readonly id: number;
        //     readonly type: number;
        //   };
        //   readonly package_id: number;
        //   readonly price_id: number;
        //   readonly time_valid_end: number;
        //   readonly time_valid_start: number;
        // };
        // readonly duration: {
        //   readonly cycle: number;
        //   readonly id: number;
        //   readonly type: number;
        // };
        // readonly eligible: boolean;
        // readonly id: number;
        // readonly is_default: boolean;
      };
      // readonly time_valid_start: number;
      // readonly trial_duration: {
      //   readonly cycle: number;
      //   readonly id: number;
      //   readonly type: number;
      // };
      // readonly trial_landing_url: string;
      // readonly trial_landing_url_params: {
      //   readonly TrialUpsell2: boolean;
      //   readonly duration_cycle: number;
      //   readonly duration_type: number;
      //   readonly package: number;
      //   readonly trial: string;
      // };
      // readonly trial_package: {
      //   readonly category_id: number;
      //   readonly display_name: string;
      //   readonly enabled: boolean;
      //   readonly id: number;
      //   readonly internal_name: string;
      //   readonly order: number;
      // };
      // readonly trial_package_id: number;
      // readonly type: string;
    };
    readonly trialPrice: string;
    readonly withTax: boolean;
    readonly with_GST: boolean;
  };
}

export interface WebsocketCompleteReviewEvent
  extends BaseServerNotificationsData {
  readonly type: 'completeReview';
  readonly data: {
    readonly id: number; // Project id
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly invoice_id: number | null;
    // readonly isEmployer: boolean;
    readonly message: string;
    // readonly myRating?: string;
    readonly name: string;
    readonly private: boolean;
    readonly publicName: string;
    readonly rating: string;
    readonly seoUrl: string | number | null;
    readonly userName: string;
    // readonly accountId: number;
    // readonly categoryName: string;
    // readonly isHourly: boolean;
    readonly userId: number;
  };
}

export interface WebsocketSuggestionForFreelancerAfterReceiveReviewEvent
  extends BaseServerNotificationsData {
  readonly type: 'suggestionForFreelancerAfterReceiveReview';
  readonly data: {
    readonly projectId: number;
    readonly projectName: string;
    readonly seoUrl: string | number | null;
    readonly img: {
      readonly profile_logo_url?: string;
    };
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly aggregate: string;
    // readonly userId: number;
  };
}

export interface WebsocketUpgradeToNonFreeMembershipEvent
  extends BaseServerNotificationsData {
  readonly type: 'upgradeToNonFreeMembership';
  readonly data: {
    readonly currencyCode: string;
    readonly currentPlan: string | null;
    readonly freeTrial: boolean | null;
    readonly newPlan: string;
    readonly price: string | number | null;
    readonly refundedAmount: number | null;
    readonly start: string | null;
    readonly end: string | null;
    readonly tax: string | null;
    readonly timeUnit: string | null;
    readonly type: string;
    // readonly membership_mid: boolean;
    // readonly remainingBids: string;
    // readonly showBiddingPromotion: boolean;
    // readonly ctaUrl: string;
    // readonly isDowngrade: boolean;
    // readonly trialDays: number;
  };
}

export interface WebsocketContestReviewPostedEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestreviewposted';
  readonly state?: string;
  readonly data: {
    readonly id: string | number; // Project id
    readonly img?: { readonly logo_url?: string | boolean };
    readonly name: string;
    readonly publicName: string;
    readonly seoUrl: string;
    readonly userId: number;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly accountId: number; // Review receiver user id
    // readonly assign_to?: string; // TODO: Enum -> 'Freelancer'
    // readonly contest_review_id: number; // Even contest id => freelancer to employer
    // readonly time: number;
  };
}

export interface WebsocketActivateFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'activateFreelancer';
  readonly data: {
    readonly id: number; // User id
    readonly key: string;
    readonly updatedProfile: string; // 'false'
    readonly verifiedEmail: string; // 'false'
  };
}

export interface WebsocketContestCompleteEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestComplete';
  readonly data: {
    // TODO: T37457 There's more in event_notify.php
    readonly contest_name: string;
    readonly contest_url: string;
    readonly entry_id: string;
    readonly imgUrl: string;
    readonly public_name: string;
    readonly review_id: string;
    readonly to_freelancer: boolean;
    readonly user_name: string;
    // readonly contest_id: string;
    // readonly to_user_id: string | number;
    // readonly from_user_id: string;
    // readonly id: string;
    // readonly time: number;
    // readonly user_profile_url: string;
  };
}

export interface WebsocketContestEntryEngagedEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryEngaged';
  readonly data: {
    readonly contestId: number;
    readonly totalEngageableEntries: number;
    readonly unratedEntries: number;
    readonly engagementRatio: number;
  };
}

export interface WebsocketContestEntryCreatedEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestEntryCreated';
  readonly data: {
    readonly contestId: string;
    readonly contestName: string;
    readonly contestSeoUrl: string;
    readonly entryUrl: string;
    readonly freelancerId: string | number;
    readonly freelancerImg?: { readonly logo_url?: string | boolean };
    readonly freelancerName: string;
    readonly freelancerPublicName: string;
    readonly entryNumber: string;
    readonly entryThumb: string;
    readonly entryS3path: string;
    readonly entryId: string | number;
    readonly userId: string | number;
    readonly time: number;
    readonly sellPrice: number;
    readonly entryThumbName: string;
    readonly entryTitle: string;
    readonly entryFiles: readonly ContestEntryFile[];
    readonly status: ContestEntryStatusWebsocketEvent;
    readonly upgrades: EntryUpgradesApi;
    readonly timeEntered: number;
    readonly employerId: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly currencyCode: string;
    // readonly currencyId: string;
    // readonly currencySign: string;
    // readonly entriesUrl: string;
    // readonly entryName: string;
    // readonly freelancerImg: string;
    // readonly hasWinner: boolean;
    // readonly userName: string;
    // readonly img: string;
    // readonly publicName: string;
  };
}

export interface WebsocketKYCEvent extends BaseServerNotificationsData {
  readonly type: 'kyc';
  readonly data: {
    readonly country?: string;
    readonly isCorporate?: string; // 'true', 'false'
    readonly status: KYCStatus;
  };
}

export interface WebsocketVerificationRequestEvent
  extends BaseServerNotificationsData {
  readonly type: 'verificationRequestRejected';
  readonly data: {
    readonly verificationFileIds: readonly string[];
  };
}

export interface WebsocketReviewActivateEvent
  extends BaseServerNotificationsData {
  readonly type: 'reviewActivate';
  readonly data: {
    readonly currencyId: string | number;
    readonly id: number;
    readonly img: { readonly profile_logo_url: string };
    readonly isAutomaticPayments: boolean;
    readonly isHourly: boolean;
    readonly isMilestoneCreated: boolean;
    readonly isQHP: boolean | null;
    readonly name: string;
    readonly notShowEmployerFee: boolean | null;
    readonly projectCreateType: QHPCreateType | null;
    readonly quickhireFreelancerId: string | number | null;
    readonly quickhireFreelancerPublicName: string | null;
    readonly quickhireFreelancerUsername: string | null;
    readonly seoUrl: string | number | null;
    // TODO: T37457 There's more in event_notify.php
    // readonly currencySign: string;
    // readonly categoryName: string;
    // readonly currencyCode: string;
  };
}

export interface WebsocketContestPCBEvent extends BaseServerNotificationsData {
  readonly type: 'contest_pcb';
  readonly data: {
    readonly contestName: string;
    readonly seoUrl: string;
    readonly userPublicName: string;
    readonly username: string;
    readonly imgUrl?: string;
    readonly contestId: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly hash: string;
    // readonly id: number;
    // readonly userUrl: string;
  };
}

export interface WebsocketContestCommentEvent
  extends BaseServerNotificationsData {
  readonly type:
    | 'contestCommentPosted'
    | 'contestCommentDeleted'
    | 'contestCommentUndeleted';
  readonly data: {
    readonly commentId: number;
    readonly parentCommentId?: number;
  };
}

export interface WebsocketCustomAdminNotificationEvent
  extends BaseServerNotificationsData {
  readonly type: 'customAdminNotification';
  readonly data: {
    readonly link_text: string;
    readonly link_url: string;
    readonly noti_description: string;
    readonly noti_text: string;
    readonly id: string; // Usually an empty string
    // readonly weight: string | null;
  };
}

export interface WebsocketSendDisputeMessageEvent
  extends BaseServerNotificationsData {
  readonly type: 'sendDisputeMessage';
  readonly data: {
    readonly dispute_id: string;
    readonly id: number; // Project id
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly message: string;
    readonly name: string; // Project name
    readonly publicName: string;
    readonly userName: string;
    readonly userId: string;
  };
}

export interface WebsocketUpdateMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'updateMilestone';
  readonly data: {
    readonly accountId: number;
    readonly amount: number;
    readonly bidId: number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly description: string;
    readonly id: number; // Project id
    readonly img?: { readonly logo_url?: string | boolean };
    readonly isSelected: boolean;
    readonly name: string;
    readonly old_amount: number;
    readonly publicName: string;
    readonly requestId: number;
    readonly sellerId: string;
    readonly seoUrl: string | number | null;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly submitDate: string;
    // readonly time: number;
    // readonly userId: string;
  };
}

export interface WebsocketWelcomeEvent extends BaseServerNotificationsData {
  readonly type: 'welcome';
  readonly data: {
    readonly siteName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly img: string;
    // readonly userId: number;
  };
}

export interface WebsocketCompleteContestReviewEvent
  extends BaseServerNotificationsData {
  readonly type: 'completeContestReview';
  readonly data: {
    // TODO: T37457 There's more in event_notify.php
    readonly userId: number; // From User Id (same as accountId)
    readonly userName: string; // From Username
    readonly name: string; // Contest Name
    readonly publicName: string; // Fallback to username on PHP side
    readonly message: string;
    readonly rating: string;
    readonly img?: { readonly logo_url?: string | boolean };
    readonly seoUrl?: string;
    // readonly id?: string | number; // Project id
    // readonly contest_review_id: string | null;
    // readonly categoryName: string;
    // readonly private: boolean;
    // readonly accountId: number;
  };
}

export interface WebsocketRejectedEvent extends BaseServerNotificationsData {
  readonly type: 'rejected';
  readonly data: {
    readonly addtionalMsg?: string;
    readonly id: number;
    readonly name: string;
    readonly rejectReasonConfig?: readonly { readonly descr?: string }[];
    readonly img?: { readonly logo_url?: boolean | string };
    // TODO: T37457 There's more in event_notify.php
    // readonly rejectReasonConfig: {
    //   readonly descr: string;
    //   readonly descr_appendage: string;
    //   readonly id: string;
    //   readonly order_id: string;
    // };
    // readonly linkUrl: string;
    // readonly seoUrl: string | number | null;
  };
}

export interface WebsocketPrizeDispersedFreelancerEvent
  extends BaseServerNotificationsData {
  readonly type: 'prizeDispersedFreelancer';
  readonly data: {
    readonly contest_name: string;
    readonly contest_url: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly employer_name: string;
    // readonly employer_profile_url: string;
    // readonly http_pic_url_head: string;
    // readonly https_pic_url_head: string;
    // readonly pic_url_tail: string;
    // readonly time: number;
  };
}

export interface WebsocketNewDisputeEvent extends BaseServerNotificationsData {
  readonly type: 'newDispute';
  readonly data: {
    readonly accountId: number;
    readonly dispute_id: number;
    readonly id: number;
    readonly imgUrl?: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName: string;
    readonly reason?: string;
    readonly time: number;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly currencycode: string;
    // readonly currencysign: string;
    // readonly userId: string;
  };
}

export interface WebsocketUploadFileEvent extends BaseServerNotificationsData {
  readonly type: 'uploadFile';
  readonly data: {
    readonly file: {
      readonly id: string | number;
      readonly name: string;
      readonly size: string;
      readonly isDriveFile?: boolean;
    };
    readonly id: string; // Bid id
    readonly img?: { readonly logo_url?: string | boolean };
    readonly projectId: number;
    readonly name: string;
    readonly publicName: string;
    readonly userName: string;
    readonly userId: number;
    readonly seoUrl: string | number | null;
    readonly time: number;
    // TODO: T37457 There's more in event_notify.php
    // readonly accountId: number;
    // readonly file: {
    //   readonly delete_type: string;
    //   readonly delete_url: string;
    //   readonly error: string;
    //   readonly filesize: number;
    //   readonly id: number;
    //   readonly isDriveFile: boolean;
    //   readonly name: string;
    //   readonly s3_obj: string;
    //   readonly submitdate: string;
    //   readonly thumb_obj: string;
    //   readonly thumbnail_url: string;
    //   readonly type: string;
    //   readonly url: string;
    // };
  };
}

export interface WebsocketReleasePartMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'releasePartMilestone';
  readonly data: {
    readonly accountId: number;
    readonly amount: string;
    readonly bidId: number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly id: number; // Project id
    readonly img: { readonly profile_logo_url?: string };
    readonly leftAmount: number;
    readonly linkUrl: string;
    readonly name: string;
    readonly otherReason: string;
    readonly publicName: string;
    readonly tranId: number;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly seoUrl: string | number | null;
    // readonly reason: string;
    // readonly sellerId: string;
    // readonly submitDate: string;
    // readonly userId: number;
  };
}

export interface WebsocketInvoicePaidEvent extends BaseServerNotificationsData {
  readonly type: 'invoicePaid';
  readonly data: {
    readonly amount: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly id: string; // Project id
    readonly img: { readonly profile_logo_url: string };
    readonly invoiceId: string | number;
    readonly name: string;
    readonly publicName: string;
    readonly seoUrl: string;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly accountId: string;
    // readonly amountPayable: string;
    // readonly amountReceived: string;
    // readonly amountUnpaid: string;
    // readonly dueDate: string;
    // readonly projIsHourly: boolean;
    // readonly sellerId: string;
    // readonly userId: string;
  };
}

export interface WebsocketInvoiceFeedbackEvent
  extends BaseServerNotificationsData {
  readonly type: 'invoiceFeedback';
  readonly data: {
    readonly accountId: string;
    readonly id: string; // Project id
    readonly invoiceId?: number;
    readonly img: { readonly profile_logo_url: string };
    readonly name: string;
    readonly seoUrl: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly amount: string;
    // readonly currencyCode: string;
    // readonly currencyId: string;
    // readonly currencySign: string;
    // readonly dueDate: string;
    // readonly sellerId: string;
    // readonly userName: string;
  };
}

export interface WebsocketPendingFundsEvent
  extends BaseServerNotificationsData {
  readonly type: 'pendingFunds';
  readonly data: {
    readonly amount: string;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly descr: string;
    readonly message: string;
    readonly reason: string;
  };
}

export interface WebsocketEmailChangeEvent extends BaseServerNotificationsData {
  readonly type: 'emailChange';
  readonly data: {
    readonly img: { readonly profile_logo_url?: string };
    readonly new_email: string;
    readonly requestId: number;
    readonly ipAddress: string | null;
  };
}

export interface WebsocketSecurePhoneUpdatedEvent
  extends BaseServerNotificationsData {
  readonly type: 'securePhoneUpdated';
  readonly data: {
    readonly id_verification_required: boolean;
    readonly secure_phone: SecurePhoneGetResultApi;
  };
}

export interface WebsocketEmailChangeConfirmedEvent
  extends BaseServerNotificationsData {
  readonly type: 'emailChangeConfirmed';
  readonly data: {
    readonly newEmail: string;
    readonly requestId: number;
  };
}

export interface WebsocketMakeDisputeOfferEvent
  extends BaseServerNotificationsData {
  readonly type: 'makeDisputeOffer';
  readonly data: {
    readonly amount: string | number;
    readonly currency: {
      readonly code: string;
      readonly sign: string;
    };
    readonly dispute_id: string;
    readonly id: number; // Project id
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName: string;
    readonly sender_role: string;
    readonly userName: string;
    // readonly time: number;
    // readonly userId: string;
  };
}

export interface WebsocketProjectHireMeExpiredEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectHireMeExpired';
  readonly data: {
    readonly id: number; // Project id
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName: string;
    readonly userName: string;
    readonly autoRepostProjectId?: number;
    readonly autoRepostProjectName?: string;
    readonly jobsURL: string;
    readonly seoUrl: string;
    readonly userId: number;
  };
}

export interface WebsocketProjectInviteToBid
  extends BaseServerNotificationsData {
  readonly type: 'projectInviteToBid';
  readonly data: {
    readonly projectId: number;
    readonly freelancerId: number;
  };
}

export interface WebsocketEmailVerifiedEvent
  extends BaseServerNotificationsData {
  readonly type: 'emailVerified';
  readonly data: {};
}

export interface WebsocketAcceptDisputeOfferEvent
  extends BaseServerNotificationsData {
  readonly type: 'acceptDisputeOffer';
  readonly data: {
    readonly dispute_id: string;
    readonly id: number;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName: string;
    readonly userId: string;
    readonly userName: string;
  };
}

export interface WebsocketEscalateDisputeEvent
  extends BaseServerNotificationsData {
  readonly type: 'escalateDispute';
  readonly data: {
    // readonly currencycode: string;
    // readonly currencysign: string;
    readonly dispute_id: string;
    // readonly id?: number; // Not in ES schema but sent from event_notify
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string; // project name
    readonly publicName: string;
    readonly time: number;
    readonly userName: string;
  };
}

export interface WebsocketProjectEmailVerificationRequiredEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectEmailVerificationRequiredEvent';
  readonly data: {
    readonly userId: number;
    readonly projectId?: number;
  };
}

export interface WebsocketWinningDesignReadyForReviewEvent
  extends BaseServerNotificationsData {
  readonly type: 'winningDesignReadyForReview';
  readonly data: {
    readonly autoReleasePrizeTime: number;
    readonly contest_name: string;
    readonly contest_url: string;
    readonly entry_id: string;
    readonly imgUrl: string;
    readonly winner_name: string;
    readonly winner_profile_url: string;
    readonly winner_publicName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly http_pic_url_head: string;
    // readonly https_pic_url_head: string;
    // readonly id: string; // Entry id
    // readonly pic_url_tail: string;
    // readonly time: number;
    // readonly userId: string;
    // readonly userName: string;
  };
}

export interface WebsocketEditAwardedBidAcceptedEvent
  extends BaseServerNotificationsData {
  readonly type: 'editAwardedBidAccepted';
  readonly data: {
    // TODO: T37457 There's more in event_notify.php
    readonly buyerName: string;
    readonly buyerPublicName: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly projName: string;
    readonly editBidDetails: {
      readonly newAmount: number;
      readonly newPeriod: number;
      readonly oldAmount: number;
      readonly oldPeriod: number;
      readonly bidID: number;
      readonly comment: string;
      // readonly formattedRequestTime: string;
      // readonly formattedResponseTime: boolean;
      readonly id: number;
      readonly requestTime: number;
      readonly responseTime?: number | false;
      readonly status: BidEditRequestStatusApi;
    };
    readonly id: number; // Project id
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly linkUrl: string;
    // readonly userName: string;
    // readonly bid: {
    //   readonly abtest_enable_exam_link: boolean;
    //   readonly bid_based_project_status: {
    //     readonly description: string;
    //     readonly name: string;
    //   };
    //   readonly completed: boolean;
    //   readonly descr: string;
    //   readonly descr_html: string;
    //   readonly descr_short: string;
    //   readonly descr_short_html: string;
    //   readonly expert_guarantee: {
    //     readonly amount: string;
    //     readonly bid_id: string;
    //     readonly bidder_id: string;
    //     readonly currency: {
    //       readonly code: string;
    //       readonly exchange_rate: string;
    //       readonly id: string;
    //       readonly name: string;
    //       readonly sign: string;
    //     };
    //     readonly id: string;
    //     readonly project_id: string;
    //     readonly status: string;
    //     readonly time_created: number;
    //   };
    //   readonly fix_project_hourly_rate: number;
    //   readonly highlighted: boolean;
    //   readonly id: number;
    //   readonly milestone_percentage: string;
    //   readonly no: string;
    //   readonly paid_status: boolean;
    //   readonly period: string;
    //   readonly project_id: string;
    //   readonly submitdate: string;
    //   readonly submitdate_f2: string;
    //   readonly submitdate_f: string;
    //   readonly submitdate_ts: number;
    //   readonly sum: string;
    //   readonly users_id: string;
    // };
    // readonly currencyId: string;
    // readonly descr: string;
    // readonly imgUrl: string;
    // readonly projectId: number;
    // readonly publicName: string;
    // readonly text: string;
    // readonly time: number;
    // readonly title: string;
    // readonly userId: string;
  };
}

export interface WebsocketEditAwardedBidCreatedEvent
  extends BaseServerNotificationsData {
  readonly data: WebsocketEditAwardedBidAcceptedEvent['data'];
  readonly type: 'editAwardedBidRequest';
}

export interface WebsocketEditAwardedBidDeclinedEvent
  extends BaseServerNotificationsData {
  readonly data: WebsocketEditAwardedBidAcceptedEvent['data'];
  readonly type: 'editAwardedBidDeclined';
}

export interface WebsocketAdminForceVerifyPhoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'adminForceVarifyPhone';
  readonly data: {
    readonly img: {
      readonly profile_logo_url: string;
    };
  };
}

export interface WebsocketRemindUseBidsEvent
  extends BaseServerNotificationsData {
  readonly type: 'remindUseBids';
  readonly data: {
    readonly img: {
      readonly profile_logo_url: string;
    };
    readonly username: string;
    readonly bids_remaining: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly currency_code: string;
    // readonly currency_sign: string;
    // readonly membership: string; // This is an array in event_notify
    // readonly membership_package_id: string | number;
    // readonly time: number;
  };
}

export interface WebsocketContestCreatedEvent
  extends BaseServerNotificationsData {
  readonly type: 'contestCreated';
  readonly data: {
    // TODO: T37457 There's more in event_notify.php
    readonly contest_name: string;
    readonly contest_url: string;
    readonly contest_id: string;
    // readonly id: string; // Contest id
  };
}

export interface WebsocketProjectSharedWithYouEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectSharedWithYou';
  readonly data: {
    readonly actingInviterPublicName?: string | null;
    readonly actingInviterUsername?: string | null;
    readonly employerPublicName: string;
    readonly employerUsername: string;
    readonly projectId: number;
    readonly projectName: string;
  };
}

export interface WebsocketPostDraftProjectEvent
  extends BaseServerNotificationsData {
  readonly type: 'postDraftProject';
  readonly data: {
    readonly projectId: number;
    readonly projectName: string;
  };
}

export interface WebsocketPrizeDispersedEmployerEvent
  extends BaseServerNotificationsData {
  readonly type: 'prizeDispersedEmployer';
  readonly data: {
    readonly contest_name: string;
    readonly contest_url: string;
    readonly freelancer_publicname?: string;
    readonly freelancer_username?: string;
    readonly hasWinner: boolean;
    // TODO: T37457 There's more in event_notify.php
    // readonly freelancer_url?: string;
  };
}

export interface WebsocketDirectTransferDoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'directTransferDone';
  readonly data: {
    // TODO: T37457 There's more in event_notify.php
    readonly amount: string;
    readonly currencycode: string;
    // readonly currencysign: string;
    readonly id: number; // Project id
    readonly img: { readonly profile_logo_url: string };
    readonly name: string;
    readonly otherReason: string;
    readonly publicName: string;
    readonly seoUrl: string | number | null;
    readonly userName: string;
    readonly userId: string;
  };
}

export interface WebsocketInvoiceRequestedChangeEvent
  extends BaseServerNotificationsData {
  readonly type: 'invoiceRequestChange';
  readonly data: {
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencyCode: string;
    readonly img: { readonly profile_logo_url: string };
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly accountId: string | number;
    // readonly currencySign: string;
    // readonly dueDate: string;
    // readonly id: string | number;
    // readonly imgUrl: string;
    // readonly invoiceId: string | number;
    // readonly linkUrl: string;
    // readonly sellerId: string | number;
    // readonly state?: string;
    // readonly userId: string | number;
  };
}

export interface WebsocketInvoiceWithdrawnEvent
  extends BaseServerNotificationsData {
  readonly type: 'invoiceWithdrawn';
  readonly data: {
    readonly amount: string | number;
    readonly currencyCode: string;
    readonly img: { readonly profile_logo_url: string };
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php
    // readonly accountId: string | number;
    // readonly bidId: string | number;
    // readonly dueDate: string;
    // readonly id: string | number;
    // readonly imgUrl: string;
    // readonly invoiceId: string | number;
    // readonly linkUrl: string;
    // readonly sellerId: string | number;
    // readonly state?: string;
    // readonly userId: string | number;
    // readonly currencySign: string;
  };
}

export interface WebsocketGroupChatEvent extends BaseServerData {
  readonly type: 'groupchat';
  // This "should" be messages but it is send from GAF, via event_notify.php,
  // and not from the messages service.
  readonly parent_type: 'notifications';
  readonly data: {
    readonly action: 'add' | 'remove';
    readonly payload: {
      readonly members: readonly number[];
      readonly thread: number;
    };
  };
}

export interface WebsocketShowcaseSourceApprovalEvent extends BaseServerData {
  readonly type: 'showcaseSourceApproval';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly linkUrl: string;
    readonly projectId: number;
    readonly projectTitle: string;
    readonly projectType: ShowcaseSourceTypeApi;
  };
}

export interface WebsocketMilestoneStatusUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'milestoneStatusUpdate';
  readonly data: {
    readonly sellerId: number;
    readonly buyerId: number;
    readonly projectId: number;
    readonly bidId: number | null;
    readonly milestoneId: number;
    readonly milestoneStatus: MilestoneStatusApi;
  };
}

export interface WebsocketRequestCancelMilestoneEvent
  extends BaseServerNotificationsData {
  readonly type: 'requestCancelMilestone';
  readonly data: {
    readonly milestoneId: number;
    readonly actionReason: MilestoneCancelRequestReasonApi;
    readonly actionReasonText: string;
  };
}

export interface WebsocketInvoiceSummaryUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'invoiceSummaryUpdate';
  readonly data: {
    readonly invoiceId: number;
    readonly totalHours: number;
    readonly hoursAmount: number;
    readonly resultAmount: number;
    readonly otherAmount: number;
  };
}

export interface WebsocketBidHourlyCurrentCycleSummaryUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'bidHourlyCurrentCycleSummaryUpdate';
  readonly data: {
    readonly bidId: number;
    readonly totalTrackedTime: number;
    readonly totalUninvoicedTime: number;
  };
}

export interface WebsocketIpContractUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'ipContractUpdate';
  readonly data: {
    readonly ipContract: IpContractApi;
  };
}

export interface WebsocketIPAgreementUpgradeEvent
  extends BaseServerNotificationsData {
  readonly type: 'ipAgreementUpgrade';
  readonly data: {
    readonly projectId: number;
  };
}

export interface WebsocketNDAUpgradeEvent extends BaseServerNotificationsData {
  readonly type: 'ndaUpgrade';
  readonly data: {
    readonly projectId: number;
  };
}

export interface WebsocketNDASignedEvent extends BaseServerNotificationsData {
  readonly type: 'ndaSigned';
  readonly data: {
    readonly projectId: number;
    readonly time: number;
    readonly userId: number;
  };
}

export interface WebsocketUserReportsActionBidRestrictionEvent
  extends BaseServerNotificationsData {
  readonly type: 'userReportsActionBidRestriction';
  readonly data: {
    readonly id: number;
    readonly action: string;
    readonly startDate: number;
    readonly endDate: number;
  };
}

export interface WebsocketHourlyContractUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'hourlyContractUpdate';
  readonly data: {
    readonly hourlyContract: UndefinedFieldsToNull<HourlyContractApi>;
  };
}

export interface WebsocketTimeTrackingSessionCreateEvent
  extends BaseServerNotificationsData {
  readonly type: 'timeTrackingSessionCreate';
  readonly data: {
    readonly id: number;
    readonly time_started: number;
    readonly invoice_id?: number;
    readonly time_last_updated: number;
    readonly manually_tracked: boolean;
    readonly user_id: number;
    readonly project_id: number;
    readonly trackee_id?: number;
    readonly bid_id: number;
    readonly note?: string;
    readonly invoiceable_time: number;
  };
}

export interface WebsocketTimeTrackingSessionDeleteEvent
  extends BaseServerNotificationsData {
  readonly type: 'timeTrackingSessionDelete';
  readonly data: {
    readonly id: number;
  };
}

export interface WebscoketAddProjectCollaboratorEvent
  extends BaseServerNotificationsData {
  readonly type: 'addProjectCollaborator';
  readonly data: ProjectCollaborationApi;
}

export interface WebsocketEditProjectCollaboratorPermissionsEvent
  extends BaseServerNotificationsData {
  readonly type: 'editProjectCollaboratorPermissions';
  readonly data: {
    readonly id: number;
    readonly projectId: number;
    readonly permissions: readonly ProjectCollaborationPermissionApi[];
  };
}

export interface WebsocketOpenHireMeForBiddingEvent
  extends BaseServerNotificationsData {
  readonly type: 'openHireMeForBidding';
  readonly data: {
    readonly projectId: number;
  };
}

export interface WebsocketRevokeProjectCollaboratorPermissionsEvent
  extends BaseServerNotificationsData {
  readonly type: 'revokeProjectCollaborator';
  readonly data: {
    readonly id: number;
  };
}

export interface WebsocketRejectMilestoneCancellationEvent
  extends BaseServerNotificationsData {
  readonly type: 'rejectMilestoneCancellation';
  readonly data: {
    readonly id: number;
  };
}

export interface WebsocketProjectStatusChangeEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectStatusChange';
  readonly data: {
    readonly id: number;
    readonly newStatus: {
      readonly formattedState: {
        readonly desc: string;
        readonly name: string;
      };
      readonly info: {
        readonly opened: boolean;
        readonly closed: boolean;
        readonly closed_cancelled?: boolean;
        readonly closed_expired?: boolean;
        readonly closed_accepted?: boolean;
        readonly frozen: boolean;
        readonly frozen_bidtimeout?: boolean;
        readonly frozen_waiting?: boolean;
        readonly pending: boolean;
        readonly rejected: boolean;
      };
      // project state from db table
      readonly state: 'A' | 'C' | 'F' | 'D' | 'P' | 'R';
      readonly status: ProjectStatusApi;
      readonly subStatus:
        | ProjectSubStatusApi
        | false
        | null
        | 'requires_correction';
    };
  };
}

export interface WebsocketTrackPoint {
  readonly id: number;
  readonly trackId: number;
  readonly latitude: number;
  readonly longitude: number;
  readonly timeCreated: number;
}

export interface WebsocketTrackUpdateEvent extends BaseServerNotificationsData {
  readonly type: 'trackUpdate';
  readonly data: WebsocketTrackPoint;
}

export interface WebsocketTrackCreateEvent extends BaseServerNotificationsData {
  readonly type: 'trackCreate';
  readonly data: {
    readonly id: number;
    readonly userId: number;
    readonly projectId: number;
    readonly timeStarted: number;
    readonly timeFinished?: number;
    readonly duration: number;
    readonly invoiceId: number | null;
    readonly trackPoints: readonly WebsocketTrackPoint[];
  };
}

export interface WebsocketProfileNameUpdateEvent extends BaseServerData {
  readonly type: 'profileNameUpdate';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly firstName: string;
    readonly lastName: string;
  };
}

export interface WebsocketProfilePictureUpdateEvent
  extends BaseServerNotificationsData {
  readonly type: 'profilePictureUpdate';
  readonly data: {
    readonly avatar: string;
    readonly avatarLarge: string;
  };
}

export interface GroupsCommentUpdateEvent {
  readonly updateType: 'comment.update';
  readonly payload: {
    readonly commentFeed: ThreadApi;
  };
}

export interface GroupsCommentCreateEvent {
  readonly updateType: 'comment.create';
  readonly payload: {
    readonly commentFeed: ThreadApi;
  };
}

export interface GroupsPostCreateEvent {
  readonly updateType: 'post.create';
  readonly payload: {
    readonly feedItem: FeedItemApi;
    readonly post: PostApi;
    /**
     * This is only set if post has been created by the user
     * depending on post owner. We need this value in order to know
     * if new WS post is from current logged in user on frontend.
     * */
    readonly ownerUserId?: number;
  };
}

export interface GroupsUpdateEvent {
  readonly updateType: 'group.update';
  readonly payload: {
    readonly group: GroupApi;
  };
}

export interface GroupMemberRemoveEvent {
  readonly updateType: 'groupMember.remove';
  readonly payload: {
    readonly groupMember: GroupMemberApi;
  };
}

export interface GroupsCommentReactEvent {
  readonly updateType: 'comment.react';
  readonly payload: {
    readonly reaction: ReactionApi;
  };
}

export interface GroupsCommentUnreactEvent {
  readonly updateType: 'comment.unreact';
  readonly payload: {
    readonly reaction: ReactionApi;
  };
}

export interface GroupsFeedPostReactEvent {
  readonly updateType: 'post.react';
  readonly payload: {
    readonly reaction: ReactionApi;
  };
}

export interface GroupsFeedPostUnreactEvent {
  readonly updateType: 'post.unreact';
  readonly payload: {
    readonly reaction: ReactionApi;
  };
}

export type GroupsEvent =
  | GroupMemberRemoveEvent
  | GroupsCommentCreateEvent
  | GroupsCommentReactEvent
  | GroupsCommentUnreactEvent
  | GroupsCommentUpdateEvent
  | GroupsFeedPostReactEvent
  | GroupsFeedPostUnreactEvent
  | GroupsPostCreateEvent
  | GroupsUpdateEvent;

export interface ResourceEventPayload {
  readonly payload: GroupsEvent;
}

export interface WebsocketResourceEvent extends BaseServerNotificationsData {
  readonly type: 'resource';
  readonly data: ResourceEventPayload;
}

export interface WebsocketSubmitOnBehalfProjectEvent extends BaseServerData {
  readonly type: 'submitOnBehalfProject';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly onBehalfProjectId: number;
    readonly projectTitle: string;
    readonly posterId: number;
    readonly posterUsername: string;
    readonly posterDisplayName: string;
    readonly nominatedEmail: string;
    readonly nominatedUserDisplayName?: string;
    readonly nominatedUserUsername?: string;
    readonly imgUrl?: string;
  };
}

export interface WebsocketPostOnBehalfProjectEvent extends BaseServerData {
  readonly type: 'postOnBehalfProject';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly projectTitle: string;
    readonly projectId: number;
    readonly projectOwnerDisplayName: string;
    readonly projectOwnerUsername: string;
    readonly imgUrl?: string;
  };
}

export interface WebsocketRejectOnBehalfProjectEvent extends BaseServerData {
  readonly type: 'rejectOnBehalfProject';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly onBehalfProjectId: number;
    readonly projectTitle: string;
    readonly nominatedUserDisplayName: string;
    readonly nominatedUserUsername: string;
    readonly nominatedUserId: number;
    readonly imgUrl?: string;
  };
}

export interface WebsocketCartItemsUpdateEvent extends BaseServerData {
  readonly type: 'cartItemsUpdate';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly cartItems: readonly CartItemApi[];
  };
}

export interface WebsocketUserStatusUpdateEvent extends BaseServerData {
  readonly type: 'userStatusUpdate';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly status: UserStatusApi;
  };
}

export interface WebsocketClientViewedBidEvent extends BaseServerData {
  readonly type: 'clientViewedBid';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly bidSeenCount: number;
    readonly notificationType: ClientViewedBidNotificationType;
  };
}

export interface WebsocketProjectTitleEditRequestEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectTitleEditRequest';
  readonly data: {
    readonly id: number;
    readonly project_id: number;
    readonly new_title: string;
    readonly requested_by: number;
    readonly status: ProjectTitleEditRequestStatusApi;
    readonly old_title: string;
  };
}

export interface WebsocketAcceptedProjectTitleEditRequestEvent
  extends BaseServerNotificationsData {
  readonly type: 'acceptedProjectTitleEditRequest';
  readonly data: {
    readonly id: number;
    readonly project_id: number;
    readonly new_title: string;
    readonly old_title: string;
  };
}

export interface WebsocketRejectedProjectTitleEditRequestEvent
  extends BaseServerNotificationsData {
  readonly type: 'rejectedProjectTitleEditRequest';
  readonly data: {
    readonly id: number;
    readonly project_id: number;
    readonly new_title: string;
    readonly old_title: string;
  };
}

export interface WebsocketRenamedProjectTitleEvent
  extends BaseServerNotificationsData {
  readonly type: 'renamedProjectTitle';
  readonly data: {
    readonly projectId: number;
    readonly newTitle: string;
    readonly oldTitle: string;
    readonly projectOwnerId: number;
  };
}

export interface WebsocketProjectTipSentEvent
  extends BaseServerNotificationsData {
  readonly type: 'projectTipSent';
  readonly data: {
    readonly projectId: number;
    readonly employerId: number;
    readonly amount: number;
  };
}

export interface WebsocketProjectBiddingClosingSoonEvent
  extends BaseServerData {
  readonly type: 'projectBiddingClosingSoon';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly projectId: number;
    readonly projectSeoUrl: string;
    readonly projectTitle: string;
  };
}

export interface WebsocketVerificationRequestChangeEvent
  extends BaseServerData {
  readonly type: 'verificationRequestChange';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly verificationRequest: VerificationRequestAjax;
  };
}
